/**
 * Runs one index query and returns it keyed by index name.
 *
 * Only options that are explicitly provided are forwarded to Algolia and
 * mirrored into `state`, so the payload matches what each call site produced
 * before this was generalised.
 *
 * @param client - The un-patched Algolia client owned by the runtime.
 * @param indexName - The physical index name.
 * @param query - The query options.
 * @returns The initial-results entry for this index.
 */
export async function buildInitialResults(client, indexName, query) {
    const { disjunctiveFacets = [], query: q = '', page = 0, hitsPerPage, filters, ruleContexts, facets = ['*'], maxValuesPerFacet, getRankingInfo, clickAnalytics, analyticsTags } = query;
    const optional = {
        ...(filters !== undefined ? { filters } : {}),
        ...(ruleContexts !== undefined ? { ruleContexts } : {})
    };
    const response = await client.searchSingleIndex({
        indexName,
        searchParams: {
            query: q,
            page,
            hitsPerPage,
            ...optional,
            facets,
            ...(maxValuesPerFacet !== undefined ? { maxValuesPerFacet } : {}),
            ...(getRankingInfo !== undefined ? { getRankingInfo } : {}),
            ...(clickAnalytics !== undefined ? { clickAnalytics } : {}),
            ...(analyticsTags !== undefined ? { analyticsTags } : {})
        }
    });
    // results[0] = main query, results[1..N] = one per disjunctive facet.
    const results = [response, ...disjunctiveFacets.map(() => response)];
    return {
        [indexName]: {
            state: {
                index: indexName,
                query: q,
                page,
                hitsPerPage,
                ...optional,
                ...(clickAnalytics !== undefined ? { clickAnalytics } : {}),
                ...(analyticsTags !== undefined ? { analyticsTags } : {}),
                disjunctiveFacets
            },
            results
        }
    };
}
/**
 * Reports whether the server-fetched state for an index was built with the same
 * `filters` the client is about to configure — i.e. whether hydrating from it
 * is safe.
 *
 * A missing entry counts as a mismatch, so callers fall back to a fresh search.
 *
 * @param initialResults - The initial-results map, typically `page.data.algolia`.
 * @param indexName - The physical index name to look up.
 * @param filters - The filter string the client will apply.
 * @returns True when the server data matches the client's filters.
 */
export function serverFiltersMatch(initialResults, indexName, filters) {
    const entry = initialResults?.[indexName];
    if (!entry)
        return false;
    const serverFilters = entry.state?.filters ?? '';
    return serverFilters === filters;
}
